/**
 * FormErrorSummary
 *
 * Displays a summary of form validation errors at the top of a form.
 * Uses the invite flow error styling pattern (light red background,
 * red left border, role="alert" for accessibility).
 *
 * Works with both Formik forms and generic error arrays.
 */

import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import './style.scss';

interface FormErrorSummaryProps {
  /** Error messages — accepts string array or Formik errors object */
  errors: string[] | Record<string, unknown>;
  /** Whether the summary is visible */
  visible: boolean;
  /** Heading override. Defaults to the field-validation wording. */
  title?: string;
  /** Optional className override */
  className?: string;
}

const FormErrorSummary: React.FC<FormErrorSummaryProps> = ({
  errors,
  visible,
  title,
  className = '',
}) => {
  const { t } = useTranslation();
  const summaryRef = useRef<HTMLDivElement>(null);
  const hasScrolledRef = useRef(false);

  // Normalize errors to a flat array of strings.
  // Handles: string[], plain object values, or non-array input.
  const errorList = (() => {
    const raw = Array.isArray(errors) ? errors : Object.values(errors || {});
    const strings = raw.filter((e): e is string => typeof e === 'string');
    return strings.filter(
      (error, index) => strings.indexOf(error) === index,
    );
  })();

  // Scroll to the error summary once when it first becomes visible.
  // Reset the flag when visibility is toggled off so it scrolls again next time.
  useEffect(() => {
    if (visible && errorList.length > 0 && !hasScrolledRef.current && summaryRef.current) {
      summaryRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      hasScrolledRef.current = true;
    }
    if (!visible) {
      hasScrolledRef.current = false;
    }
  }, [visible, errorList.length]);

  if (!visible || errorList.length === 0) {
    return null;
  }

  return (
    <div
      ref={summaryRef}
      className={`FormErrorSummary ${className}`.trim()}
      role="alert"
    >
      <p className="FormErrorSummary__title">
        {title ?? t(
          'form.error_summary_title',
          'Niet alle velden zijn correct ingevuld:',
        )}
      </p>
      <ul className="FormErrorSummary__list">
        {errorList.map((error) => (
          <li key={error} className="FormErrorSummary__item">
            {error}
          </li>
        ))}
      </ul>
    </div>
  );
};

export default FormErrorSummary;
